import React, { useMemo, useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaBell,
  FaBook,
  FaBuilding,
  FaCheck,
  FaEnvelope,
  FaFileInvoice,
  FaHandshake,
  FaHome,
  FaInfoCircle,
  FaLayerGroup,
  FaLock,
  FaMapMarkedAlt,
  FaSlidersH,
  FaUserFriends,
  FaUsers,
  FaUpload,
} from "react-icons/fa";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import SettingsTabsShell from "./components/SettingsTabsShell";
import SettingsDojos from "./tabs/Dojos/SettingsDojos";
import SettingsGroups from "./tabs/Groups/SettingsGroups";
import SettingsTeam from "./tabs/Team/SettingsTeam";
import SettingsDisciplines from "./tabs/Disciplines/SettingsDisciplines";
import SettingsAssociation from "./tabs/Association/SettingsAssociation";
import SettingsHomeContent from "./tabs/HomeContent/SettingsHomeContent";
import SettingsHomeSections from "./tabs/HomeSections/SettingsHomeSections";
import SettingsInfoClub from "./tabs/InfoClub/SettingsInfoClub";
import SettingsDossierInscription from "./tabs/DossierInscription/SettingsDossierInscription";
import SettingsCoffreFort from "./tabs/CoffreFort/SettingsCoffreFort";
import SettingsPaiements from "./tabs/Paiements/SettingsPaiements";
import SettingsReglementInterieur from "./tabs/ReglementInterieur/SettingsReglementInterieur";
import SettingsPartenaires from "./tabs/Partenaires/SettingsPartenaires";
import SettingsCommunication from "./tabs/Communication/SettingsCommunication";
import SettingsModules from "./tabs/Modules/SettingsModules";
import UploadOrphansMaintenanceCard from "./tabs/UploadOrphansMaintenance/UploadOrphansMaintenanceCard";

import {
  getStoredUserInfo,
  isValidSettingsTab,
} from "./utils/settingsDashboardUtils";

/*
  À adapter uniquement si tes anciens composants ne sont pas dans :
  src/pages/Admin/Settings/
*/
// import SettingsGroups from "../../../Admin/Settings/SettingsGroups";
// import SettingsForm from "../../../Admin/Settings/SettingsForm";
// import HomeSettingsForm from "../../../Admin/Settings/HomeSettingsForm";
// import PaiementSettingsForm from "../../../Admin/Settings/PaiementSettingsForm";
// import TeamManagement from "../../../Admin/Settings/TeamTable";
// import DisciplinesTable from "../../../Admin/Settings/DisciplinesTable";
// import InfoClubForm from "../../../Admin/Settings/InfoClubForm";
// import ModuleSettingsForm from "../../../Admin/Settings/ModuleSettingsForm";
// import ReglementInterieurForm from "../../../Admin/Settings/ReglementInterieurForm";
// import SettingsPartenaires from "../../../Admin/Settings/SettingsPartenaires";
// import SettingsDossierInscription from "../../../Admin/Settings/SettingsDossierInscription";
// import SettingsNotifications from "../../../Admin/Settings/SettingsNotifications";
// import SettingsMessages from "../../../Admin/Settings/SettingsMessages";
// import SettingsCoffreFort from "../../../Admin/Settings/SettingsCoffreFort";

function UnauthorizedSettingsPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Paramètres du club
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function SettingsDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const tabs = useMemo(
    () => [
      {
        key: "dojos",
        label: "Dojos",
        group: "Structure club",
        Icon: FaMapMarkedAlt,
        Component: SettingsDojos,
        description:
          "Lieux de pratique, adresses, photos et ordre d’affichage.",
      },
      {
        key: "groupes",
        label: "Groupes",
        group: "Structure club",
        Icon: FaUsers,
        Component: SettingsGroups,
        description: "Groupes de cours et affectations.",
      },
      {
        key: "equipes",
        label: "Équipes",
        group: "Structure club",
        Icon: FaUserFriends,
        Component: SettingsTeam,
        description: "Membres de l’équipe, enseignants et encadrants.",
      },
      {
        key: "disciplines",
        label: "Disciplines",
        group: "Structure club",
        Icon: FaBook,
        Component: SettingsDisciplines,
        description: "Disciplines proposées par le club.",
      },
      {
        key: "association",
        label: "Association",
        group: "Identité du club",
        Icon: FaBuilding,
        Component: SettingsAssociation,
        description: "Informations générales de l’association.",
      },
      {
        key: "pageAccueil",
        label: "Page Accueil",
        group: "Site internet",
        Icon: FaHome,
        Component: SettingsHomeContent,
        description: "Header, textes, section à propos et carrousel photo.",
      },
      {
        key: "dispositionAccueil",
        label: "Disposition Accueil",
        group: "Site internet",
        Icon: FaLayerGroup,
        Component: SettingsHomeSections,
        description:
          "Ordre, visibilité et médias des sections de la page d’accueil.",
      },
      {
        key: "pageInfoClub",
        label: "Info Club",
        group: "Site internet",
        Icon: FaInfoCircle,
        Component: SettingsInfoClub,
        description: "Encart d’informations importantes affiché sur l’accueil.",
      },
      {
        key: "dossier",
        label: "Dossier Inscription",
        group: "Inscription",
        Icon: FaFileInvoice,
        Component: SettingsDossierInscription,
        description:
          "Segments, informations et fichiers du dossier d’inscription.",
      },
      {
        key: "coffreFort",
        label: "Coffre-fort numérique",
        group: "Identité du club",
        Icon: FaLock,
        Component: SettingsCoffreFort,
        description:
          "Documents obligatoires, affichages, diplômes, assurances et prévention.",
      },
      {
        key: "paiements",
        label: "Paiements",
        group: "Inscription",
        Icon: FaFileInvoice,
        Component: SettingsPaiements,
        description: "Échéancier de paiement et image par défaut du règlement.",
      },
      {
        key: "reglementInterieur",
        label: "Règlement Intérieur",
        group: "Identité du club",
        Icon: FaFileInvoice,
        Component: SettingsReglementInterieur,
        description: "Texte du règlement intérieur et visuels associés.",
      },
      {
        key: "partenaires",
        label: "Partenaires",
        group: "Site internet",
        Icon: FaHandshake,
        Component: SettingsPartenaires,
        description:
          "Logos, liens, ordre d’affichage et pages détail des partenaires.",
      },
      {
        key: "communication",
        label: "Communication",
        group: "Communication",
        Icon: FaEnvelope,
        Component: SettingsCommunication,
        description: "Nettoyage des notifications et messages anciens.",
      },
      {
        key: "uploadOrphansMaintenance",
        label: "Upload Orphans Maintenance",
        group: "Maintenance",
        Icon: FaUpload,
        Component: UploadOrphansMaintenanceCard,
        description: "Gestion des fichiers orphelins.",

      },
      {
        key: "modules",
        label: "Modules",
        group: "Site internet",
        Icon: FaLayerGroup,
        Component: SettingsModules,
        description: "Activation ou désactivation des modules par rôle.",
      },
    ],
    [],
  );

  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem("settings_dashboard_active_tab");
    return isValidSettingsTab(saved, tabs) ? saved : "dojos";
  });

  useEffect(() => {
    if (!isValidSettingsTab(activeTab, tabs)) {
      setActiveTab("dojos");
      return;
    }

    localStorage.setItem("settings_dashboard_active_tab", activeTab);
  }, [activeTab, tabs]);

  if (roleId !== 1) {
    return <UnauthorizedSettingsPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-5 sm:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Administration
                </p>

                <h1 className="mt-1 flex items-center gap-3 text-2xl font-bold text-slate-900 dark:text-white">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                    <FaSlidersH />
                  </span>
                  Paramètres du club
                </h1>

                <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Centralisez tous les paramètres du site, du club, des modules,
                  des inscriptions, des paiements et de la communication.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
                <span className="rounded-2xl bg-slate-100 px-3 py-2 text-center text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                  {tabs.length} onglets
                </span>

                <span className="rounded-2xl bg-indigo-50 px-3 py-2 text-center text-xs font-bold text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                  Configuration globale
                </span>
              </div>
            </div>
          </div>
        </section>

        <SettingsTabsShell
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>

      <ToastContainer />
    </section>
  );
}
